import { useEffect, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	InputAdornment,
	Paper,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import useStyles from "./styles";
import api from "../../../services/api";
import DatagridComponent from "../../../components/datagridComponent";
import corNeutraChip from "../corNeutraChip";
import alvoId from "../alvoId";
import rotuloAlvo from "../rotuloAlvo";

export default function ModalAtualizarScripts({
	open,
	onClose,
	scripts,
	chave,
	clienteId,
	proxy,
	nomeCliente,
	saudeColeta,
	onConcluido,
}) {
	const [carregandoElegibilidade, setCarregandoElegibilidade] = useState(true);
	const [acessosVm, setAcessosVm] = useState({ manager: new Set(), proxy: new Set() });
	const [selecionados, setSelecionados] = useState([]);
	const [confirmado, setConfirmado] = useState(false);
	const [busca, setBusca] = useState("");
	const [enviando, setEnviando] = useState(false);
	const [resumo, setResumo] = useState(null);
	const [erroEnvio, setErroEnvio] = useState(null);
	const theme = useTheme();
	const styles = useStyles(theme);

	// Ao abrir, checa uma unica vez quais clientes tem acesso VM cadastrado - lista pequena
	// (so os candidatos desta abertura), nao a base toda
	useEffect(() => {
		if (!open) return;
		buscarElegibilidadeVm();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// GET /versionamento/scripts/acessosVm - devolve dois conjuntos de clienteIds: com acesso VM ao host
	// principal (manager) e com acesso VM de tag Proxy (regionais). Falha na chamada vira Sets vazios,
	// tratado como "nenhum elegivel" (nunca deixa passar um alvo cuja elegibilidade nao foi possivel
	// checar, mesmo principio que ModalConfirmacao ja usa pra divergencia de templates)
	async function buscarElegibilidadeVm() {
		setCarregandoElegibilidade(true);
		setResumo(null);
		setErroEnvio(null);
		setConfirmado(false);
		let acessos = { manager: new Set(), proxy: new Set() };
		try {
			const response = await api.get("/versionamento/scripts/acessosVm");
			acessos = {
				manager: new Set(response?.data?.clienteIdsManager || []),
				proxy: new Set(response?.data?.clienteIdsProxy || []),
			};
		} catch {
			acessos = { manager: new Set(), proxy: new Set() };
		}
		setAcessosVm(acessos);
		// Selecao padrao: todo candidato nao bloqueado (divergencia + elegibilidade) comeca marcado -
		// usa os Sets locais (nao o state, que ainda nao foi atualizado nesta mesma execucao)
		const naoBloqueados = candidatos.filter(
			(candidato) => !divergenciaDoCliente(candidato) && temAcessoVm(candidato, acessos),
		);
		setSelecionados(naoBloqueados.map((candidato) => alvoId(candidato)));
		setCarregandoElegibilidade(false);
	}

	// POST /versionamento/scripts/atualizar - manda os alvos (cliente + proxy) sempre explicitos (a selecao
	// ja filtrada pelos nao-bloqueados), nunca deixa o backend resolver sozinho a partir so de chaves
	async function handleConfirmar() {
		setEnviando(true);
		setErroEnvio(null);
		try {
			const response = await api.post("/versionamento/scripts/atualizar", {
				chaves: chave ? [chave] : [],
				alvos: candidatos
					.filter((candidato) => selecionados.includes(alvoId(candidato)))
					.map((candidato) => ({ clienteId: candidato?.clienteId, proxy: candidato?.proxy ?? "" })),
			});
			setResumo(response?.data?.resumo || []);
		} catch (error) {
			setErroEnvio(
				error?.response?.data?.error ||
					"Não foi possível enviar a atualização. Tente novamente.",
			);
		} finally {
			setEnviando(false);
		}
	}

	// Fecha o modal - se ja havia resultado de uma atualizacao enviada, dispara o refresh do pai antes
	function handleFechar() {
		if (enviando) return;
		if (resumo) {
			onConcluido?.();
		}
		onClose?.();
	}

	// Pra um alvo (cliente + proxy), lista os scripts "Desatualizado" (troca) e "Ausente no cliente" (instala) dele -
	// e exatamente o que a atualizacao desse alvo vai baixar (mesmo criterio do backend em atualizarScriptCliente.js).
	// No modo por script (chave definida), escopa so nesse script - os outros do alvo nao sao afetados
	function scriptsQueVaoMudar(alvo) {
		return (scripts || [])
			.filter((script) => !chave || script?.chave === chave)
			.map((script) => {
				const doCliente = script?.clientes?.find((c) => alvoId(c) === alvoId(alvo));
				if (doCliente?.status !== "Desatualizado" && doCliente?.status !== "Ausente no cliente") return null;
				return {
					chave: script?.chave,
					nome: script?.nome,
					versaoAtual: doCliente?.versaoCliente,
					versaoReferencia: script?.versaoReferencia,
				};
			})
			.filter(Boolean);
	}

	// Bloqueio por divergencia (preview - a garantia real e sempre o backend, que recheca na hora;
	// isso e so pra desabilitar/avisar antes de enviar)
	function divergenciaDoCliente(alvo) {
		const saude = (saudeColeta || []).find((c) => alvoId(c) === alvoId(alvo));
		// Qualquer status diferente de "sucesso" bloqueia - inclui null (nunca coletado), nao so "falha"
		if (!saude || saude?.status !== "sucesso") {
			return saude?.motivo || "Coleta deste cliente ainda não teve sucesso.";
		}
		if (saude?.itensObsoletos) {
			return saude?.motivo || "Dados de versão obsoletos.";
		}
		return null;
	}

	// Elegibilidade por acesso VM: proxy regional precisa de acesso com a tag Proxy, o principal do acesso do manager
	function temAcessoVm(alvo, acessos = acessosVm) {
		return alvo?.proxy ? acessos?.proxy?.has(alvo?.clienteId) : acessos?.manager?.has(alvo?.clienteId);
	}

	// Bloqueio combinado - divergencia de coleta ou falta de acesso VM cadastrado
	function bloqueado(alvo) {
		return Boolean(divergenciaDoCliente(alvo)) || !temAcessoVm(alvo);
	}

	// Motivo textual do bloqueio, usado no Tooltip e dentro do Chip "Bloqueado"
	function motivoBloqueio(alvo) {
		const divergencia = divergenciaDoCliente(alvo);
		if (divergencia) return divergencia;
		if (!temAcessoVm(alvo)) {
			return alvo?.proxy
				? "Nenhum acesso VM com a tag Proxy cadastrado para este cliente."
				: "Acesso VM não cadastrado para este cliente.";
		}
		return "";
	}

	// Par fundo/texto do status do resultado pos-envio - mesmos tokens de chip.background/colorText,
	// sem token novo pra abortado_divergencia (corNeutraChip, mesmo par usado em "Ausente no cliente"/"Sem versão")
	function coresResultado(status) {
		if (status === "sucesso") {
			return {
				background: theme.palette.chip.background.success,
				color: theme.palette.chip.colorText.success,
			};
		}
		if (status === "falha") {
			return {
				background: theme.palette.chip.background.critical,
				color: theme.palette.chip.colorText.critical,
			};
		}
		return corNeutraChip(theme);
	}

	// Um dos dois modos: por script (varios alvos desatualizados) ou por alvo (um so, ja escolhido - cliente + proxy)
	const scriptSelecionado = chave ? (scripts || []).find((s) => s?.chave === chave) : null;
	const candidatos = clienteId
		? [{ clienteId, proxy: proxy ?? "", nome: nomeCliente }]
		: (scriptSelecionado?.clientes || []).filter(
				(c) => c?.status === "Desatualizado" || c?.status === "Ausente no cliente",
			);

	// Rotulo "Cliente · Proxy" por alvo, pra nomear as linhas do resultado (que so trazem clienteId + proxy)
	const nomePorCliente = new Map(
		candidatos.map((candidato) => [alvoId(candidato), rotuloAlvo(candidato)]),
	);

	// Filtragem por nome do cliente ou do proxy, sempre disponivel (sem limiar de tamanho da lista)
	const candidatosFiltrados = candidatos.filter((candidato) =>
		rotuloAlvo(candidato)?.toLowerCase()?.includes(busca.toLowerCase()),
	);

	const rows = candidatosFiltrados.map((candidato) => ({
		...candidato,
		id: alvoId(candidato),
		rotulo: rotuloAlvo(candidato),
	}));

	const columns = [
		{ field: "rotulo", headerName: "Cliente", flex: 1, minWidth: 160 },
		{
			field: "scriptsAfetados",
			headerName: "Scripts afetados",
			flex: 2,
			minWidth: 260,
			renderCell: (params) => {
				const afetados = scriptsQueVaoMudar(params.row);
				if (afetados.length === 0) return "—";
				return (
					<Box sx={styles.listaScriptsAfetados}>
						{afetados.map((script) => (
							<Typography key={script.chave} variant="caption" sx={styles.itemScriptAfetado}>
								{script.chave}: {script.versaoAtual ?? "não instalado"} → {script.versaoReferencia}
							</Typography>
						))}
					</Box>
				);
			},
		},
		{
			field: "bloqueado",
			headerName: "Bloqueado",
			width: 170,
			renderCell: (params) =>
				bloqueado(params.row) ? (
					<Tooltip title={motivoBloqueio(params.row)}>
						<Chip
							size="small"
							label="Bloqueado"
							sx={{
								...styles.chipBloqueado,
								backgroundColor: theme.palette.chip.background.high,
								color: theme.palette.chip.colorText.high,
							}}
						/>
					</Tooltip>
				) : null,
		},
	];

	// Grid de candidatos, com selecao via checkbox nativo - extraido pra nao duplicar as options entre
	// o caso "com altura fixa" e o caso "altura automatica" (poucos candidatos)
	const gridCandidatos = (
		<DatagridComponent
			rows={rows}
			columns={columns}
			sxDatagrid={styles.gridScriptsAfetados}
			options={{
				checkboxSelection: true,
				disableRowSelectionOnClick: true,
				isRowSelectable: (params) => !bloqueado(params.row) && !enviando,
				rowSelectionModel: selecionados,
				onRowSelectionModelChange: (novaSelecao) => {
					setSelecionados(novaSelecao);
					setConfirmado(false);
				},
			}}
		/>
	);

	return (
		<Dialog open={open} onClose={handleFechar} fullWidth maxWidth="md">
			<DialogTitle>
				Atualizar scripts — {chave ? scriptSelecionado?.nome || chave : rotuloAlvo({ nome: nomeCliente, proxy })}
			</DialogTitle>
			<DialogContent>
				{carregandoElegibilidade && (
					<Box sx={styles.loadingContainer}>
						<CircularProgress size={28} />
					</Box>
				)}
				{!carregandoElegibilidade && resumo === null && (
					<Box>
						<TextField
							size="small"
							placeholder="Buscar cliente..."
							value={busca}
							onChange={(event) => setBusca(event.target.value)}
							sx={styles.buscaField}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search fontSize="small" />
									</InputAdornment>
								),
							}}
						/>
						{/* Paper outlined (borda desenhada, sem sombra) - mesmo padrao do ModalConfirmacao */}
						<Paper variant="outlined" sx={styles.paper}>
							{/* Altura fixa so quando o grid tem mais de 4 linhas - mesmo limiar do autoHeight
							interno do DatagridComponent */}
							{rows.length > 4 ? (
								<Box sx={styles.gridContainer}>{gridCandidatos}</Box>
							) : (
								gridCandidatos
							)}
						</Paper>
						<Box sx={styles.confirmacaoContainer}>
							<FormControlLabel
								control={
									<Checkbox
										checked={confirmado}
										disabled={enviando}
										onChange={(event) => setConfirmado(event.target.checked)}
									/>
								}
								label={`Confirmo que quero executar a atualização em ${selecionados.length} alvo(s) (cliente · proxy)`}
							/>
						</Box>
						{erroEnvio && (
							<Typography variant="body2" sx={styles.erro}>
								{erroEnvio}
							</Typography>
						)}
					</Box>
				)}
				{!carregandoElegibilidade && resumo !== null && (
					<Box sx={styles.resultadoContainer}>
						{resumo.length === 0 && (
							<Typography variant="body2" sx={styles.semItens}>
								Nenhum cliente foi processado.
							</Typography>
						)}
						{resumo.map((linha) => {
							const cor = coresResultado(linha?.status);
							return (
								<Box key={alvoId(linha)} sx={styles.linhaResultado}>
									<Typography variant="body2" sx={styles.nomeResultado}>
										{nomePorCliente.get(alvoId(linha)) || linha?.clienteId}
									</Typography>
									<Chip
										size="small"
										label={linha?.status}
										sx={{
											...styles.chip,
											backgroundColor: cor.background,
											color: cor.color,
										}}
									/>
									{linha?.status !== "sucesso" && linha?.motivo && (
										<Typography variant="caption" sx={styles.motivoResultado}>
											{linha.motivo}
										</Typography>
									)}
									{linha?.status === "sucesso" && linha?.recoletaAviso && (
										<Typography variant="caption" sx={styles.avisoResultado}>
											{linha.recoletaAviso}
										</Typography>
									)}
								</Box>
							);
						})}
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				{resumo === null ? (
					<>
						<Button onClick={handleFechar} disabled={enviando}>
							Cancelar
						</Button>
						<Tooltip
							title={
								!enviando && (!confirmado || selecionados.length === 0)
									? "Selecione ao menos um cliente e marque a confirmação abaixo para habilitar"
									: ""
							}
							disableHoverListener={enviando || (confirmado && selecionados.length > 0)}
						>
							<span>
								<Button
									variant="contained"
									disabled={enviando || !confirmado || selecionados.length === 0}
									onClick={handleConfirmar}
								>
									{enviando ? <CircularProgress size={20} /> : "Confirmar atualização"}
								</Button>
							</span>
						</Tooltip>
					</>
				) : (
					<Button variant="contained" onClick={handleFechar}>
						Fechar
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}
